import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Paper,
  LinearProgress,
} from "@mui/material";
import {
  AutoAwesome,
  TrendingUp,
  Category,
  Article,
  People,
  Analytics,
  Image,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { categoryService, statsService } from "../../api/services";
import { safeArray } from "../../utils/validation";

const StatCard = ({ title, value, icon, color }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 4,
      boxShadow: 3,
      transition: "transform 0.3s",
      "&:hover": { transform: "scale(1.03)" },
    }}
  >
    <CardContent
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Typography variant="h6" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
      </Box>
      <Box sx={{ fontSize: 40, color }}>{icon}</Box>
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleNavigation = (path) => {
    navigate(`/admin/${path}`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, statRes] = await Promise.all([
          categoryService.getCategories(),
          statsService.getStats(),
        ]);
        setCategories(safeArray(catRes.data));
        setStats(safeArray(statRes.data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalViews = stats.reduce((sum, s) => sum + (s.views || 0), 0);
  const totalRevenue = stats.reduce(
    (sum, s) => sum + parseFloat(s.ad_revenue || 0),
    0
  );

  // Estadísticas de imágenes
  const imageStats = {
    total_images: stats?.total_images || 0,
    ai_generated: stats?.ai_generated_images || 0,
    placeholder_images: stats?.placeholder_images || 0,
  };

  if (loading) return <LinearProgress />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Bienvenido, {user?.first_name || user?.email}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Vistas Totales"
            value={totalViews.toLocaleString()}
            icon={<Analytics />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Ingresos"
            value={`$${totalRevenue.toFixed(2)}`}
            icon={<TrendingUp />}
            color="#388e3c"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Categorías"
            value={categories.length}
            icon={<Category />}
            color="#f57c00"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Posts Activos"
            value={stats.length}
            icon={<Article />}
            color="#7b1fa2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Imágenes IA"
            value={imageStats.ai_generated}
            icon={<Image />}
            color="#ff9800"
          />
        </Grid>
      </Grid>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 4 }}>
        <Typography variant="h6" gutterBottom>
          Acciones Rápidas
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              label: "Generar con IA",
              icon: <AutoAwesome />,
              action: () => handleNavigation("ia"),
            },
            {
              label: "Configurar Auto-Generación",
              icon: <TrendingUp />,
              action: () => handleNavigation("auto-generation"),
            },
            {
              label: "Gestionar Categorías",
              icon: <Category />,
              action: () => handleNavigation("categories"),
            },
            {
              label: "Ver Estadísticas",
              icon: <Analytics />,
              action: () => handleNavigation("analytics"),
            },
          ].map((btn, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={btn.icon}
                onClick={btn.action}
                sx={{ py: 2, borderRadius: 3 }}
              >
                {btn.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
}
